import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { TablePagination } from '../../components/ui/table-pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, AlertTriangle, Eye, CheckCircle, XCircle, Search,
  ShieldCheck, DollarSign, MessageSquare, Clock, User
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

function DisputesTab() {
  const [disputes, setDisputes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  const [resolution, setResolution] = useState({ status: '', resolution: '', admin_notes: '' });

  const fetchDisputes = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (filter !== 'all') params.append('status', filter);
      if (searchTerm) params.append('search', searchTerm);
      const response = await axios.get(`${API}/admin/disputes?${params}`);
      setDisputes(response.data.disputes || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, filter, searchTerm]);

  useEffect(() => { fetchDisputes(); }, [fetchDisputes]);
  
  // Debounced search - reset to page 1 when search term changes
  useEffect(() => {
    const timer = setTimeout(() => { if (page !== 1) setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, page]);

  const handleResolve = async () => {
    if (!resolution.status) { toast.error('Please select a status'); return; }
    try {
      await axios.put(`${API}/admin/disputes/${selectedDispute.id}`, resolution);
      toast.success('Dispute updated successfully!');
      setSelectedDispute(null);
      setResolution({ status: '', resolution: '', admin_notes: '' });
      fetchDisputes();
    } catch (error) {
      toast.error('Failed to update dispute');
    }
  };

  const getStatusColor = (status) => {
    const map = { open: 'bg-red-100 text-red-700', investigating: 'bg-amber-100 text-amber-700', resolved: 'bg-green-100 text-green-700', closed: 'bg-stone-100 text-stone-500' };
    return map[status] || 'bg-stone-100 text-stone-500';
  };

  const openCount = disputes.filter(d => d.status === 'open').length;

  return (
    <>
      <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input placeholder="Search by booking ID or reason..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-11" data-testid="dispute-search" />
        </div>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {['all', 'open', 'investigating', 'resolved', 'closed'].map((status) => (
          <button key={status} onClick={() => { setFilter(status); setPage(1); }}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${filter === status ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-stone-100'}`}
            data-testid={`filter-${status}`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status === 'open' && openCount > 0 && <span className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">{openCount}</span>}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div></div>
        ) : disputes.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <p className="text-slate-700 font-medium">No disputes found</p>
            <p className="text-slate-500 text-sm mt-1">All clear! No issues to review.</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-stone-100">
              {disputes.map((dispute) => (
                <div key={dispute.id} className="p-6 hover:bg-stone-50" data-testid={`dispute-${dispute.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(dispute.status)}`}>{dispute.status}</span>
                        <span className="text-xs text-slate-500">{new Date(dispute.created_at).toLocaleDateString()}</span>
                      </div>
                      <h3 className="font-bold text-slate-900">{dispute.reason}</h3>
                      <p className="text-slate-600 mt-1 line-clamp-2">{dispute.description}</p>
                      {dispute.booking_id && (
                        <div className="flex gap-4 mt-4 text-sm">
                          <div><span className="text-slate-500">Booking:</span> <span className="font-mono text-slate-700">{dispute.booking_id.slice(0, 8)}...</span></div>
                        </div>
                      )}
                      {dispute.resolution && (
                        <div className="mt-4 p-3 bg-green-50 rounded-xl"><p className="text-sm text-green-800"><strong>Resolution:</strong> {dispute.resolution}</p></div>
                      )}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => { setSelectedDispute(dispute); setResolution({ status: dispute.status, resolution: dispute.resolution || '', admin_notes: '' }); }} data-testid={`view-dispute-${dispute.id}`}>
                      <Eye className="w-4 h-4 mr-2" />View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <TablePagination page={page} totalPages={totalPages} total={total} limit={limit} onPageChange={setPage} onLimitChange={setLimit} />
          </>
        )}
      </div>

      {selectedDispute && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900 font-[Poppins]">Dispute Details</h2>
                <button onClick={() => setSelectedDispute(null)} className="p-2 hover:bg-stone-100 rounded-lg"><XCircle className="w-5 h-5 text-slate-400" /></button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-4 bg-stone-50 rounded-xl"><p className="text-sm text-slate-500 mb-1">Reason</p><p className="font-bold text-slate-900">{selectedDispute.reason}</p></div>
              <div className="p-4 bg-stone-50 rounded-xl"><p className="text-sm text-slate-500 mb-1">Description</p><p className="text-slate-700">{selectedDispute.description}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-stone-50 rounded-xl"><p className="text-sm text-slate-500 mb-1">Booking ID</p><p className="font-mono text-slate-700 text-sm">{selectedDispute.booking_id || 'N/A'}</p></div>
                <div className="p-4 bg-stone-50 rounded-xl"><p className="text-sm text-slate-500 mb-1">Created</p><p className="text-slate-700">{new Date(selectedDispute.created_at).toLocaleString()}</p></div>
              </div>
              <hr className="my-4" />
              <div>
                <Label>Status</Label>
                <Select value={resolution.status} onValueChange={(value) => setResolution(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger className="w-full h-12 mt-1.5" data-testid="dispute-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="investigating">Investigating</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Resolution (for customer)</Label>
                <textarea value={resolution.resolution} onChange={(e) => setResolution(prev => ({ ...prev, resolution: e.target.value }))} placeholder="Describe how this was resolved..." className="w-full h-24 mt-1.5 border border-stone-200 rounded-lg px-3 py-2 resize-none" data-testid="dispute-resolution-input" />
              </div>
              <div>
                <Label>Admin Notes (internal)</Label>
                <textarea value={resolution.admin_notes} onChange={(e) => setResolution(prev => ({ ...prev, admin_notes: e.target.value }))} placeholder="Internal notes..." className="w-full h-24 mt-1.5 border border-stone-200 rounded-lg px-3 py-2 resize-none" data-testid="dispute-notes-input" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setSelectedDispute(null)} className="flex-1">Cancel</Button>
                <Button onClick={handleResolve} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="save-dispute-btn">Update Dispute</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function WarrantyClaimsTab() {
  const [claims, setClaims] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  const [resolution, setResolution] = useState({ status: '', admin_resolution: '', admin_notes: '', refund_amount: '' });

  const fetchClaims = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (filter !== 'all') params.append('status', filter);
      if (searchTerm) params.append('search', searchTerm);
      const response = await axios.get(`${API}/admin/warranty-claims?${params}`);
      setClaims(response.data.claims || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Failed to fetch warranty claims:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, filter, searchTerm]);

  useEffect(() => { fetchClaims(); }, [fetchClaims]);
  
  // Debounced search - reset to page 1 when search term changes
  // Debounced search - reset to page 1 when search term changes
  useEffect(() => {
    const timer = setTimeout(() => { if (page !== 1) setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, page]);

  const handleResolve = async () => {
    if (!resolution.status) { toast.error('Please select a status'); return; }
    try {
      const payload = {
        status: resolution.status,
        admin_resolution: resolution.admin_resolution || null,
        admin_notes: resolution.admin_notes || null,
        refund_amount: resolution.refund_amount ? parseFloat(resolution.refund_amount) : null
      };
      await axios.put(`${API}/admin/warranty-claims/${selectedClaim.id}`, payload);
      toast.success('Warranty claim updated!');
      setSelectedClaim(null);
      setResolution({ status: '', admin_resolution: '', admin_notes: '', refund_amount: '' });
      fetchClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update claim');
    }
  };

  const getStatusColor = (status) => {
    const map = {
      open: 'bg-red-100 text-red-700',
      provider_responded: 'bg-blue-100 text-blue-700',
      investigating: 'bg-amber-100 text-amber-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-rose-100 text-rose-700',
      closed: 'bg-stone-100 text-stone-500'
    };
    return map[status] || 'bg-stone-100 text-stone-500';
  };

  const getStatusLabel = (s) => s?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';

  const openCount = claims.filter(c => c.status === 'open' || c.status === 'provider_responded').length;

  return (
    <>
      <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input placeholder="Search by booking ID or reason..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-11" data-testid="warranty-search" />
        </div>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {['all', 'open', 'provider_responded', 'investigating', 'approved', 'rejected', 'closed'].map((status) => (
          <button key={status} onClick={() => { setFilter(status); setPage(1); }}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${filter === status ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-stone-100'}`}
            data-testid={`warranty-filter-${status}`}
          >
            {getStatusLabel(status === 'all' ? 'all' : status)}
            {status === 'open' && openCount > 0 && <span className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">{openCount}</span>}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div></div>
        ) : claims.length === 0 ? (
          <div className="p-12 text-center">
            <ShieldCheck className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <p className="text-slate-700 font-medium">No warranty claims found</p>
            <p className="text-slate-500 text-sm mt-1">No claims to review.</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-stone-100">
              {claims.map((claim) => (
                <div key={claim.id} className="p-6 hover:bg-stone-50" data-testid={`warranty-claim-${claim.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(claim.status)}`}>{getStatusLabel(claim.status)}</span>
                        <span className="text-xs text-slate-500">{new Date(claim.created_at).toLocaleDateString()}</span>
                        {claim.refund_amount > 0 && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                            <DollarSign className="w-3 h-3" />P{claim.refund_amount?.toLocaleString()}
                          </span>
                        )}
                      </div>
                      <h3 className="font-bold text-slate-900">{claim.reason}</h3>
                      <p className="text-slate-600 mt-1 line-clamp-2">{claim.description}</p>
                      <div className="flex flex-wrap gap-4 mt-3 text-sm">
                        {claim.booking_service && <span className="text-slate-500">Service: <strong className="text-slate-700">{claim.booking_service}</strong></span>}
                        <span className="text-slate-500">Booking: <span className="font-mono text-slate-700">{claim.booking_id?.slice(0, 8)}...</span></span>
                      </div>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm">
                        {claim.customer_name && <span className="flex items-center gap-1 text-slate-500"><User className="w-3.5 h-3.5" />Customer: <strong className="text-slate-700">{claim.customer_name}</strong></span>}
                        {claim.provider_name && <span className="flex items-center gap-1 text-slate-500"><User className="w-3.5 h-3.5" />Provider: <strong className="text-slate-700">{claim.provider_name}</strong></span>}
                      </div>
                      {claim.provider_response && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-xl">
                          <p className="text-xs font-medium text-blue-600 mb-1 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Provider Response</p>
                          <p className="text-sm text-blue-800">{claim.provider_response}</p>
                        </div>
                      )}
                      {claim.admin_resolution && (
                        <div className="mt-3 p-3 bg-green-50 rounded-xl"><p className="text-sm text-green-800"><strong>Resolution:</strong> {claim.admin_resolution}</p></div>
                      )}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => {
                      setSelectedClaim(claim);
                      setResolution({
                        status: claim.status,
                        admin_resolution: claim.admin_resolution || '',
                        admin_notes: claim.admin_notes || '',
                        refund_amount: claim.refund_amount || ''
                      });
                    }} data-testid={`view-warranty-${claim.id}`}>
                      <Eye className="w-4 h-4 mr-2" />Review
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <TablePagination page={page} totalPages={totalPages} total={total} limit={limit} onPageChange={setPage} onLimitChange={setLimit} />
          </>
        )}
      </div>

      {selectedClaim && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900 font-[Poppins]">Warranty Claim Review</h2>
                <button onClick={() => setSelectedClaim(null)} className="p-2 hover:bg-stone-100 rounded-lg"><XCircle className="w-5 h-5 text-slate-400" /></button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-4 bg-stone-50 rounded-xl">
                <p className="text-sm text-slate-500 mb-1">Reason</p>
                <p className="font-bold text-slate-900">{selectedClaim.reason}</p>
              </div>
              <div className="p-4 bg-stone-50 rounded-xl">
                <p className="text-sm text-slate-500 mb-1">Description</p>
                <p className="text-slate-700">{selectedClaim.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-stone-50 rounded-xl"><p className="text-sm text-slate-500 mb-1">Service</p><p className="text-slate-700 font-medium">{selectedClaim.booking_service || 'N/A'}</p></div>
                <div className="p-4 bg-stone-50 rounded-xl"><p className="text-sm text-slate-500 mb-1">Warranty Ends</p><p className="text-slate-700">{selectedClaim.warranty_end_date ? new Date(selectedClaim.warranty_end_date).toLocaleDateString() : 'N/A'}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-stone-50 rounded-xl"><p className="text-sm text-slate-500 mb-1">Customer</p><p className="text-slate-700 font-medium">{selectedClaim.customer_name || 'N/A'}</p></div>
                <div className="p-4 bg-stone-50 rounded-xl"><p className="text-sm text-slate-500 mb-1">Provider</p><p className="text-slate-700 font-medium">{selectedClaim.provider_name || 'N/A'}</p></div>
              </div>
              {selectedClaim.provider_response && (
                <div className="p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm text-blue-600 font-medium mb-1 flex items-center gap-1"><MessageSquare className="w-4 h-4" /> Provider Response</p>
                  <p className="text-blue-800">{selectedClaim.provider_response}</p>
                  {selectedClaim.provider_responded_at && <p className="text-xs text-blue-500 mt-2">Responded: {new Date(selectedClaim.provider_responded_at).toLocaleString()}</p>}
                </div>
              )}

              <hr className="my-4" />
              <div>
                <Label>Status</Label>
                <Select value={resolution.status} onValueChange={(value) => setResolution(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger className="w-full h-12 mt-1.5" data-testid="warranty-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="investigating">Investigating</SelectItem>
                    <SelectItem value="approved">Approved (Refund)</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {resolution.status === 'approved' && (
                <div>
                  <Label>Refund Amount (PHP)</Label>
                  <Input type="number" min="0" step="0.01" value={resolution.refund_amount} onChange={(e) => setResolution(prev => ({ ...prev, refund_amount: e.target.value }))} placeholder="Enter refund amount..." className="mt-1.5 h-12" data-testid="warranty-refund-amount" />
                </div>
              )}
              <div>
                <Label>Resolution (visible to both parties)</Label>
                <textarea value={resolution.admin_resolution} onChange={(e) => setResolution(prev => ({ ...prev, admin_resolution: e.target.value }))} placeholder="Describe the resolution..." className="w-full h-24 mt-1.5 border border-stone-200 rounded-lg px-3 py-2 resize-none" data-testid="warranty-resolution-input" />
              </div>
              <div>
                <Label>Admin Notes (internal)</Label>
                <textarea value={resolution.admin_notes} onChange={(e) => setResolution(prev => ({ ...prev, admin_notes: e.target.value }))} placeholder="Internal notes..." className="w-full h-24 mt-1.5 border border-stone-200 rounded-lg px-3 py-2 resize-none" data-testid="warranty-notes-input" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setSelectedClaim(null)} className="flex-1">Cancel</Button>
                <Button onClick={handleResolve} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="save-warranty-btn">
                  {resolution.status === 'approved' ? 'Approve & Refund' : 'Update Claim'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function AdminDisputes() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('disputes');

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />
      <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="w-10 h-10 rounded-full hover:bg-stone-200 flex items-center justify-center md:hidden">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]" data-testid="disputes-page-title">Disputes & Warranty</h1>
              <p className="text-slate-500">Manage complaints, issues, and warranty claims</p>
            </div>
          </div>
        </div>

        <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 shadow-sm w-fit" data-testid="disputes-tabs">
          <button
            onClick={() => setActiveTab('disputes')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'disputes' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-stone-100'}`}
            data-testid="tab-disputes"
          >
            <AlertTriangle className="w-4 h-4" />
            Disputes
          </button>
          <button
            onClick={() => setActiveTab('warranty')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'warranty' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-stone-100'}`}
            data-testid="tab-warranty"
          >
            <ShieldCheck className="w-4 h-4" />
            Warranty Claims
          </button>
        </div>

        {activeTab === 'disputes' ? <DisputesTab /> : <WarrantyClaimsTab />}
      </main>
    </div>
  );
}
